import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, UserCheck, Edit } from "lucide-react";
import type { Account, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { ContactFormModal } from "@/components/contact-form-modal";

interface BeneficialOwnersPanelProps {
  account: Account;
}

// Derived beneficial owner record from the /derived endpoint
interface DerivedBeneficialOwner {
  contactId: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactMobile: string | null;
  ownershipPercentage: string | null;
  via: string;
}

// AccountPerson type (from crm_x_account_people table) — used for legacy mutations
interface AccountPerson {
  id: string;
  accountId: string;
  contactId: string;
  role: string;
  appointmentDate: string | null;
  ceasedDate: string | null;
  contact?: Contact;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export function BeneficialOwnersPanel({ account }: BeneficialOwnersPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<AccountPerson | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const { toast } = useToast();

  // F010: Fetch derived beneficial owners (union of ABO, legacy account_people, shareholders >=25%)
  const { data: derivedOwners = [], isLoading } = useQuery<DerivedBeneficialOwner[]>({
    queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"],
  });

  // Also fetch account people to support edit/delete on legacy entries
  const { data: accountPeople = [] } = useQuery<AccountPerson[]>({
    queryKey: ["/api/crm/accounts", account.id, "people"],
  });

  // Filter legacy beneficial owners from account_people for edit/delete lookups
  const legacyBeneficialOwners = useMemo(() =>
    accountPeople.filter(person => person.role === "beneficial_owner"),
    [accountPeople]
  );

  // Existing beneficial owner contact IDs for the add dialog exclusion
  const existingBeneficialOwnerContactIds = useMemo(() => {
    return derivedOwners.map((owner) => owner.contactId);
  }, [derivedOwners]);

  // Add mutation using legacy account_people endpoint
  const addMutation = useMutation({
    mutationFn: async (data: { contactId: string }) => {
      await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
        contactId: data.contactId,
        role: "beneficial_owner",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      toast({ title: "Beneficial owner added", description: "The beneficial owner has been added successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add beneficial owner", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      toast({ title: "Beneficial owner removed", description: "The beneficial owner has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove beneficial owner", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; notes?: string }) => {
      const { id, ...updateData } = data;
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      toast({ title: "Beneficial owner updated", description: "The beneficial owner has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update beneficial owner", variant: "destructive" });
    },
  });

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedContactId("");
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingOwner(null);
  };

  const handleOpenEdit = (contactId: string) => {
    const legacyEntry = legacyBeneficialOwners.find(p => p.contactId === contactId);
    if (legacyEntry) {
      setEditingOwner(legacyEntry);
      setIsEditDialogOpen(true);
    }
  };

  const handleUpdate = () => {
    if (!editingOwner) return;
    updateMutation.mutate({
      id: editingOwner.id,
      notes: editingOwner.notes || undefined,
    });
  };

  const handleAdd = () => {
    if (!selectedContactId) return;
    addMutation.mutate({ contactId: selectedContactId });
  };

  const handleDelete = (contactId: string) => {
    const legacyEntry = legacyBeneficialOwners.find(p => p.contactId === contactId);
    if (legacyEntry) {
      setDeleteId(legacyEntry.id);
    }
  };

  // Parse via badges from comma-separated string
  const parseViaBadges = (via: string) => {
    return via.split(", ").map((label) => {
      let variant: "default" | "secondary" | "outline" = "secondary";
      if (label === "Beneficiary") variant = "default";
      if (label === "Shareholder") variant = "outline";
      return { label: `via ${label}`, variant };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="beneficial-owners-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">BENEFICIAL OWNERS</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-beneficial-owner"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Beneficial Owner
        </Button>
      </div>

      {derivedOwners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <UserCheck className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No beneficial owners yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click &quot;Add Beneficial Owner&quot; to add a beneficial owner to this entity
          </p>
        </div>
      ) : (
        <div className="border rounded-md bg-white">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Full Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Source</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Email</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Mobile</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {derivedOwners.map((owner) => {
                const fullName = [owner.contactFirstName, owner.contactLastName]
                  .filter(Boolean)
                  .join(" ") || "Unknown Contact";
                const badges = parseViaBadges(owner.via);
                // Only allow edit/delete for legacy account_people entries
                const hasLegacyEntry = legacyBeneficialOwners.some(p => p.contactId === owner.contactId);

                return (
                  <TableRow
                    key={owner.contactId}
                    className="h-10"
                    data-testid={`row-beneficial-owner-${owner.contactId}`}
                  >
                    <TableCell className="px-2 py-1 text-sm font-medium">
                      {fullName}
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      <div className="flex gap-1 flex-wrap">
                        {badges.map((badge, i) => (
                          <Badge
                            key={i}
                            variant={badge.variant}
                            className="text-xs"
                            data-testid={`badge-via-${badge.label.replace(/\s/g, '-').toLowerCase()}`}
                          >
                            {badge.label}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {owner.contactEmail || "-"}
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {owner.contactMobile || "-"}
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      {hasLegacyEntry && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEdit(owner.contactId)}
                            data-testid={`button-edit-beneficial-owner-${owner.contactId}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(owner.contactId)}
                            data-testid={`button-delete-beneficial-owner-${owner.contactId}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Dialog using ContactSelect pattern */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Beneficial Owner</DialogTitle>
            <DialogDescription>
              Select a contact from the client group to add as a beneficial owner for AML/CTF compliance.
              Beneficial ownership is based on ownership percentage, not appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact *</Label>
              <ContactSelect
                value={selectedContactId}
                onValueChange={setSelectedContactId}
                clientGroupId={account.clientGroupId}
                excludeContactIds={existingBeneficialOwnerContactIds}
                placeholder="Select a contact"
                data-testid="select-beneficial-owner-contact"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
                data-testid="button-create-new-contact-beneficial-owner"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedContactId || addMutation.isPending}
              data-testid="button-confirm-add-beneficial-owner"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Beneficial Owner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Beneficial Owner</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this beneficial owner? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-beneficial-owner"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Beneficial Owner Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Beneficial Owner</DialogTitle>
            <DialogDescription>
              Update notes for this beneficial owner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={editingOwner?.contact
                  ? `${editingOwner.contact.firstName || ""} ${editingOwner.contact.lastName || ""}`.trim()
                  : "Unknown Contact"}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={editingOwner?.notes || ""}
                onChange={(e) => setEditingOwner(prev => prev ? { ...prev, notes: e.target.value } : null)}
                placeholder="Add any relevant notes about this beneficial owner"
                rows={3}
                data-testid="edit-input-beneficial-owner-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              data-testid="button-confirm-update-beneficial-owner"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Beneficial Owner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Creation Modal */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={(newContact: Contact) => {
          setSelectedContactId(newContact.id);
          setShowCreateContactDialog(false);
          queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
          toast({
            title: "Contact created",
            description: "The contact has been created and selected. Click 'Add Beneficial Owner' to complete."
          });
        }}
        defaultClientGroupId={account.clientGroupId}
      />
    </div>
  );
}
